import { useState, useEffect } from "react";
import AdminSidebar from "@/components/AdminSidebar";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useToast } from "@/components/ui/use-toast";
import { Menu, Save, Loader2 } from "lucide-react";
import { getSystemSettings, updateMultipleSettings } from "@/api/settingsService";

interface Settings {
  siteName: string;
  siteDescription: string;
  contactEmail: string;
  emailFrom: string;
  emailFromName: string;
  maintenanceMode: boolean;
}

const AdminSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Settings>({
    siteName: 'Vermado High-End Automotive',
    siteDescription: 'Premium automotive services',
    contactEmail: 'info@vermado.nl',
    emailFrom: 'noreply@vermado.nl',
    emailFromName: 'Vermado',
    maintenanceMode: false,
  });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await getSystemSettings();
      setSettings(prev => ({
        ...prev,
        siteName: (data.siteName as string) || prev.siteName,
        siteDescription: (data.siteDescription as string) || prev.siteDescription,
        contactEmail: (data.contactEmail as string) || prev.contactEmail,
        emailFrom: (data.emailFrom as string) || prev.emailFrom,
        emailFromName: (data.emailFromName as string) || prev.emailFromName,
        maintenanceMode: (data.maintenanceMode as boolean) ?? prev.maintenanceMode,
      }));
    } catch { /* silently fail */ } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMultipleSettings([
        { key: 'siteName', value: settings.siteName, type: 'string' },
        { key: 'siteDescription', value: settings.siteDescription, type: 'string' },
        { key: 'contactEmail', value: settings.contactEmail, type: 'string' },
        { key: 'emailFrom', value: settings.emailFrom, type: 'string' },
        { key: 'emailFromName', value: settings.emailFromName, type: 'string' },
        { key: 'maintenanceMode', value: settings.maintenanceMode, type: 'boolean' },
      ]);
      toast({ title: "Opgeslagen", description: "Instellingen zijn bijgewerkt." });
    } catch { toast({ variant: "destructive", title: "Fout", description: "Kon niet opslaan." }); }
    finally { setSaving(false); }
  };

  const Field = ({ label, id, type = 'text', value, onChange, description }: { label: string; id: string; type?: string; value: string; onChange: (v: string) => void; description?: string }) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-[#202223] mb-1">{label}</label>
      {description && <p className="text-xs text-[#6d7175] mb-1.5">{description}</p>}
      <input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-300 transition-colors" />
    </div>
  );

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <main className="flex-1 overflow-auto bg-[#f6f6f7]">
          <div className="border-b border-gray-200 bg-white px-8 py-6">
            <div className="max-w-3xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SidebarTrigger className="md:hidden"><Menu className="h-5 w-5" /></SidebarTrigger>
                <h1 className="text-xl font-semibold text-[#202223]">Instellingen</h1>
              </div>
              <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 bg-[#202223] text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-[#333] disabled:opacity-50 transition-colors">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Opslaan
              </button>
            </div>
          </div>

          <div className="px-8 py-8">
            <div className="max-w-3xl mx-auto space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
              ) : (
                <>
                  {/* Algemeen */}
                  <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-gray-200/60">
                    <div className="px-5 py-4 border-b border-gray-100">
                      <h2 className="text-sm font-semibold text-[#202223]">Algemeen</h2>
                      <p className="text-xs text-[#6d7175] mt-0.5">Basis website instellingen</p>
                    </div>
                    <div className="px-5 py-5 space-y-4">
                      <Field label="Website naam" id="siteName" value={settings.siteName} onChange={(v) => setSettings(s => ({ ...s, siteName: v }))} />
                      <Field label="Beschrijving" id="siteDescription" value={settings.siteDescription} onChange={(v) => setSettings(s => ({ ...s, siteDescription: v }))} />
                      <Field label="Contact e-mail" id="contactEmail" type="email" value={settings.contactEmail} onChange={(v) => setSettings(s => ({ ...s, contactEmail: v }))} />
                      <div className="flex items-center justify-between pt-1">
                        <div>
                          <p className="text-sm font-medium text-[#202223]">Onderhoudsmodus</p>
                          <p className="text-xs text-[#6d7175]">Website tijdelijk offline zetten</p>
                        </div>
                        <button
                          onClick={() => setSettings(s => ({ ...s, maintenanceMode: !s.maintenanceMode }))}
                          className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${settings.maintenanceMode ? 'bg-[#202223]' : 'bg-gray-200'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${settings.maintenanceMode ? 'translate-x-5' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* E-mail */}
                  <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-gray-200/60">
                    <div className="px-5 py-4 border-b border-gray-100">
                      <h2 className="text-sm font-semibold text-[#202223]">E-mail</h2>
                      <p className="text-xs text-[#6d7175] mt-0.5">Afzender configuratie</p>
                    </div>
                    <div className="px-5 py-5 space-y-4">
                      <Field label="Afzender e-mail" id="emailFrom" type="email" value={settings.emailFrom} onChange={(v) => setSettings(s => ({ ...s, emailFrom: v }))} />
                      <Field label="Afzender naam" id="emailFromName" value={settings.emailFromName} onChange={(v) => setSettings(s => ({ ...s, emailFromName: v }))} />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default AdminSettings;
